/**
 * Google Ads conversion event helper.
 *
 * Google's "install the tag" flow expects the base gtag('config', ...) call
 * to live in index.html (which it does), and specific conversion events
 * to fire client-side on the "success"/"thank-you" moment for each
 * conversion action.
 *
 * We centralize this so:
 *  - The `AW-...` conversion ID doesn't get hardcoded across every page.
 *  - The `send_to` label for each action is named explicitly (Sign-up,
 *    Subscribe, etc.) instead of leaking cryptic labels into UI code.
 *  - Firing is safe even if gtag hasn't loaded yet (ad blockers, offline)
 *    — we never let analytics break the actual user flow.
 *
 * Deduplication note: `transaction_id` is sent whenever we have a stable
 * identifier (Paddle txn id for Subscribe, user_id for Sign-up). Google
 * uses it to prevent double-counting if the event fires more than once.
 */

const CONVERSION_ID = "AW-17739393214";

// Conversion action labels — each conversion action in Google Ads gets a
// unique label. Keep this map narrow and named.
//
// TODO: If Google Ads has issued a SEPARATE label for the Sign-up action
// (i.e. one that's different from `bgIeCKWH7M4cEL7R5opC`), swap it in
// here — that label lives in Google Ads → Tools → Conversions → click the
// action → "Tag setup" → "Install the tag yourself".
export const CONVERSION_LABELS = {
  signup: "bgIeCKWH7M4cEL7R5opC",
  subscribe: "bgIeCKWH7M4cEL7R5opC",
};

/**
 * Fire a Google Ads conversion event.
 *
 * @param {"signup"|"subscribe"} action  Which conversion action to fire.
 * @param {object}                [opts] Optional Google Ads params.
 * @param {string} [opts.transactionId]  Idempotency key for Google Ads dedup.
 * @param {number} [opts.value]          Monetary value (major units, e.g. 12.5).
 * @param {string} [opts.currency]       ISO-4217 currency, defaults to USD.
 */
export function fireGoogleAdsConversion(action, opts = {}) {
  try {
    const label = CONVERSION_LABELS[action];
    if (!label) {
      console.warn(`[gtag] Unknown conversion action: ${action}`);
      return;
    }
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      // gtag not loaded (ad-block, script blocked, dev with no key) —
      // silently no-op so the user flow doesn't break.
      return;
    }
    const payload = { send_to: `${CONVERSION_ID}/${label}` };
    if (opts.transactionId) payload.transaction_id = String(opts.transactionId);
    if (typeof opts.value === "number" && !Number.isNaN(opts.value)) {
      payload.value = opts.value;
      payload.currency = opts.currency || "USD";
    }
    window.gtag("event", "conversion", payload);
  } catch (e) {
    // Analytics must never break the actual product flow.
    console.warn("[gtag] conversion event failed:", e);
  }
}
